import { SIGNUP_BONUS_CREDITS, TRIAL_CREDITS } from "@convex/model/credits";

export const AppConfig = {
  // REQUIRED
  appName: "MyKaraoke Video",
  // REQUIRED: a short description of your app for SEO tags (can be overwritten)
  appDescription:
    "The all-in-one modern karaoke video creation platform, right in your browser.",
  // REQUIRED (no https://, not trialing slash at the end, just the naked domain)
  domainName: "app.mykaraoke.video",
  email: {
    // subdomain to use when sending emails, if you don't have a subdomain, just remove it. Highly recommended to have one (i.e. mg.yourdomain.com or mail.yourdomain.com)
    subdomain: "notify.mykaraoke.video",
    // REQUIRED — Email 'From' field to be used when sending magic login links
    fromNoReply: "MyKaraoke Video <noreply@notify.mykaraoke.video>",
    // REQUIRED — Email 'From' field to be used when sending other emails, like abandoned carts, updates etc..
    fromAdmin: "Emiliano at MyKaraoke Video <emiliano@mykaraoke.video>",
    // Email shown to customer if need support. Leave empty if not needed => if empty, set up Crisp above, otherwise you won't be able to offer customer support."
    supportEmail: "support@mykaraoke.video",
    // Email where to send alerts and notifications about your app (i.e. failed jobs, etc..)
    alertEmails: "alerts@mykaraoke.video",
  },
  auth: {
    // REQUIRED — the path to log in users. It's use to protect private routes (like /dashboard).
    loginUrl: "/login",
    // REQUIRED — the path you want to redirect users after successfull login (i.e. /dashboard, /private). This is normally a private page for users to manage their accounts.
    callbackUrl: "/dashboard",
  },
  stripe: {
    premiumLookupKey: "premium_month",
    premiumLookupKeyYearly: "premium_year",
    unifiedCreditLookupKey: "credit_unit",
    // All subscription price lookup keys (for fetching discount rates)
    subscriptionLookupKeys: [
      "standard_month",
      "standard_year",
      "premium_month",
      "premium_year",
    ],
  },
  featureFlags: {
    maintenanceModeEnabled: "maintenance-mode",
    premiumTrialEnabled: "premium-trial",
    forceLambdaRendersEnabled: "force-lambda-renders",
    bunnyCdnForLambdaEnabled: "bunny-cdn-lambda-renders",
    bunnyCdnForDokployEnabled: "bunny-cdn-dokploy-renders",
    chordLaneEnabled: "chord-lane",
  },
  /** API paths that stay reachable while maintenance mode is on. */
  apiMaintenanceAllowlist: ["/api/auth", "/api/version"] as const,
  trial: {
    /** Duration of the free trial in days */
    periodDays: 7,
    /** Credits included in the free trial */
    creditsIncluded: TRIAL_CREDITS,
  },
  credits: {
    /** One-time credits granted on signup */
    signupBonusCredits: SIGNUP_BONUS_CREDITS,
    purchase: {
      creditsPerPack: 50,
      minimumOrderDollars: 5,
      validityYears: 1,
    },
  },
  exports: {
    /** Default expiry for download links (days) */
    defaultExpiryDays: 30,
    /** Expiry for ProRes download links (days) */
    proResExpiryDays: 1,
  },
  limits: {
    maxVideoDuration: 1200, // 20 minutes
    maxVideoResolution: 1920 * 1080, // Full HD (1080p)
    maxProResTransparentDuration: 600, // 10 minutes in seconds
  },
  render: {
    /** Maximum concurrent renders per subscription tier */
    maxConcurrentRenders: {
      free: 1,
      standard: 2,
      premium: 5,
    },
  },
  upload: {
    // Presigned multipart URL lifetimes (seconds). R2 checks expiry when a
    // request starts, so the part URL bounds when the last part may start and
    // the complete/abort URL must outlast the whole upload. better-upload's
    // defaults (25/30 min) fail slow uploads of large files.
    multipart: {
      partSignedUrlExpiresIn: 6 * 60 * 60,
      completeSignedUrlExpiresIn: 12 * 60 * 60,
    },
    limits: {
      free: {
        media: {
          maxFileSizeMB: 50,
          maxDurationSeconds: 600, // 10 minutes
          fileTypes: ["audio/*"],
          description: "Audio files • Up to 50 MB • Max 10 minutes",
        },
        audio: {
          maxFileSizeMB: 50,
          maxDurationSeconds: 600, // 10 minutes
          fileTypes: ["audio/*"],
          description: "Audio files • Up to 50 MB • Max 10 minutes",
        },
        image: {
          maxFileSizeMB: 50,
          maxDurationSeconds: 0, // Images don't have duration
          fileTypes: ["image/jpeg", "image/png", "image/webp"],
          description: "Image files • Up to 50 MB",
        },
      },
      paid: {
        media: {
          maxFileSizeMB: 500,
          maxDurationSeconds: 1200, // 20 minutes
          fileTypes: ["audio/*", "video/*"],
          description: "Audio/video files • Up to 500 MB • Max 20 minutes",
        },
        audio: {
          maxFileSizeMB: 100,
          maxDurationSeconds: 1200, // 20 minutes
          fileTypes: ["audio/*"],
          description: "Audio files • Up to 100 MB • Max 20 minutes",
        },
        image: {
          maxFileSizeMB: 50,
          maxDurationSeconds: 0, // Images don't have duration
          fileTypes: ["image/jpeg", "image/png", "image/webp"],
          description: "Image files • Up to 50 MB",
        },
        video: {
          maxFileSizeMB: 1024,
          maxDurationSeconds: 1200, // 20 minutes
          fileTypes: ["video/mp4", "video/webm"],
          description: "Video files • Up to 1024 MB • Max 20 minutes",
        },
      },
    },
    compression: {
      image: {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.8,
      },
    },
  },
};
